import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Soft UI Dashboard React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Routes & layouts
import routes from "routes";
import DefaultLayout from "layouts/dashboard";
import Login from "layouts/authentication/sign-in";
import Page404 from "layouts/authentication/sign-in";
import Page500 from "layouts/authentication/sign-in";

import IntegrationsSuccess from "layouts/success"; 

// Soft UI Dashboard React contexts
import { useSoftUIController, setMiniSidenav, setOpenConfigurator } from "context";

import { SnackbarProvider } from "providers/SnackbarProvider"; // adjust import path

// Images
import brand from "assets/images/logos/logo.png";

const apiUrl = "https://api.getverceipt.com/api/v1";

export default function App() {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, direction, layout, openConfigurator, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // -------------------------------
  // Auth state
  // -------------------------------
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);

  // -------------------------------
  // Verify auth on mount
  // -------------------------------
  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const res = await fetch(`${apiUrl}/user/verify`, {
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });

        if (!res.ok) {
          if (res.status === 401) {
            setIsLoggedIn(false);
            navigate("/login", { replace: true });
          }
          throw new Error("Unauthorized");
        }

        const data = await res.json();
        setIsLoggedIn(data.loggedIn || false);
      } catch (err) {
        console.error("Auth check failed:", err);
        setIsLoggedIn(false);
        navigate("/login", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    verifyAuth();
  }, [navigate]);

  // -------------------------------
  // PrivateRoute wrapper
  // -------------------------------
  const PrivateRoute = ({ children }) => {
    if (loading) return <div>Loading...</div>;
    if (!isLoggedIn) return null; // navigation handled in verifyAuth
    return children;
  };
  PrivateRoute.propTypes = { children: PropTypes.node.isRequired };

  // -------------------------------
  // RTL cache
  // -------------------------------
  useMemo(() => {
    const cacheRtl = createCache({ key: "rtl", stylisPlugins: [rtlPlugin] });
    setRtlCache(cacheRtl);
  }, []);

  // -------------------------------
  // Sidenav mouse handlers
  // -------------------------------
  const handleOnMouseEnter = useCallback(() => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  }, [miniSidenav, onMouseEnter, dispatch]);

  const handleOnMouseLeave = useCallback(() => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  }, [onMouseEnter, dispatch]);

  const handleConfiguratorOpen = useCallback(
    () => setOpenConfigurator(dispatch, !openConfigurator),
    [dispatch, openConfigurator]
  );

  // -------------------------------
  // Body direction
  // -------------------------------
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // -------------------------------
  // Scroll to top on route change
  // -------------------------------
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [location.pathname]);

  // -------------------------------
  // Config button
  // -------------------------------
  /*
  const configsButton = (
    <SoftBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.5rem"
      height="3.5rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="default" color="inherit">
        settings
      </Icon>
    </SoftBox>
  );
  */

  // -------------------------------
  // Compute dashboard routes
  // -------------------------------
  const getRoutes = useCallback(
    (allRoutes) =>
      allRoutes.map((route) => {
        if (route.collapse) return getRoutes(route.collapse);
        if (route.route) return <Route key={route.key} path={route.route} element={route.component} />;
        return null;
      }),
    []
  );
  const dashboardRoutes = useMemo(() => getRoutes(routes), [getRoutes]);

  // -------------------------------
  // Themed App with routes
  // -------------------------------
  const ThemedApp = (
    <>
      {layout === "dashboard" && (
        <Sidenav
          color={sidenavColor}
          brand={brand}
          routes={routes}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        />
      )}
      {layout === "vr" && <Configurator />}
      {/*{configsButton}*/}

      <Routes>
        <Route path="/integrations/success" element={<IntegrationsSuccess/>} />

        {/* Login route */}
        <Route
          path="/login"
          element={isLoggedIn ? <Navigate to="/dashboard" replace /> : <Login setIsLoggedIn={setIsLoggedIn} />}
        />

        {/* Dashboard (private) */}
        <Route path="/dashboard/*" element={<PrivateRoute><DefaultLayout /></PrivateRoute>} />
        {dashboardRoutes}

        {/* Misc routes */}
        <Route path="/404" element={<Page404 />} />
        <Route path="/500" element={<Page500 />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Page404 />} />
      </Routes>
    </>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={themeRTL}>
        <CssBaseline />
        <SnackbarProvider>
          {ThemedApp}
        </SnackbarProvider>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={theme}>
      <CssBaseline />
        <SnackbarProvider>
          {ThemedApp}
        </SnackbarProvider>
    </ThemeProvider>
  );
}
